import { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Theme,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
} from '@mui/material';
import { CloudDownload } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useGetForecastAnalyticsQuery,
  useGetAwardedAnalyticsQuery,
  useGetProjectTypesQuery,
  useGetSubscribersQuery,
  useGetTeamContactsQuery,
  useGetTeamsQuery,
  useLazyGetForecastAnalyticsExportQuery,
  useGetRealizeRevenueAnalyticsQuery,
} from 'store/api';
import { Contact, ProjectType, Subscriber, Team } from 'store/api/models';
import {
  JFormik,
  KpiCard,
  numToPercent,
  useBreakpointQuery,
} from '@centerpiece/components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  // AnnotationPlugin,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { useIndexContext } from 'contexts/index.context';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Define the RevenueForecast type
type RevenueForecast = {
  type?: number;
  win_rate?: number;
  awarded: boolean | null;
  month?: number;
  year?: number;
  revenue_sum?: number;
  realized_revenue_sum?: number;
  bid?: boolean;
};

export const OverviewIndexPage = () => {
  const { contactRecord } = useIndexContext();
  const [team, setTeam] = useState<Team>(
    contactRecord?.form?.authorization === 'Team Leader' &&
      contactRecord?.form?.team
      ? contactRecord?.form?.team
      : ({ id: 0, name: 'All' } as Team)
  );
  const [individual, setIndividual] = useState<Contact>({
    id: 0,
    firstname: 'All',
    name: 'All',
  } as Contact & { name: string });
  const [projectType, setProjectType] = useState<ProjectType>({
    id: 0,
    name: 'All',
  } as ProjectType);
  const [numSimulations, setNumSimulations] = useState<number>(1000);
  const { data: teams = [], isLoading: teamsLoading } = useGetTeamsQuery({});
  const { data: projectTypes = [], isLoading: projectTypesLoading } =
    useGetProjectTypesQuery({});
  const { data: contacts = [] as Subscriber[], isLoading } =
    useGetSubscribersQuery({ params: { nested: 'all' } });
  const { data: kpiState = [], isFetching: kpiCardsLoading } =
    useGetAwardedAnalyticsQuery({
      params: {
        project_type: projectType?.id,
        team: team?.id,
        individual: individual?.id,
      },
    });
  const { data: forecast = [] as RevenueForecast[] } =
    useGetForecastAnalyticsQuery({
      params: {
        project_type: projectType?.id,
        team: team?.id,
        individual: individual?.id,
      },
    });
  const { data: realized_revenue = [] as RevenueForecast[] } =
    useGetRealizeRevenueAnalyticsQuery({
      params: {
        project_type: projectType?.id,
        team: team?.id,
        individual: individual?.id,
      },
    });

  const kpiCards = useMemo(
    () => [
      {
        id: 'active-quotes',
        title: 'Pre-Award',
        value: kpiState
          ?.filter((k: any) => !k.awarded && k.bid && k.state === 'Active')
          ?.reduce((acc: number, b: any) => acc + b.revenue_sum || 0, 0),
        num: kpiState
          ?.filter((k: any) => !k.awarded && k.bid && k.state === 'Active')
          ?.reduce((acc: number, b: any) => acc + b.num_projects || 0, 0),
      },
      {
        id: 'awarded-jobs',
        title: 'Awarded Jobs',
        value: kpiState
          ?.filter(
            (k: any) =>
              ((k.awarded || !k.bid) && k.state === 'Active') ||
              k.state === 'Complete'
          )
          ?.reduce((acc: number, b: any) => acc + b.revenue_sum || 0, 0),
        num: kpiState
          ?.filter(
            (k: any) =>
              ((k.awarded || !k.bid) && k.state === 'Active') ||
              k.state === 'Complete'
          )
          ?.reduce((acc: number, b: any) => acc + b.num_projects || 0, 0),
      },
      {
        id: 'pipeline',
        title: 'Total Pipeline',
        value: kpiState
          ?.filter((k: any) => k.state === 'Active' || k.state === 'Complete')
          ?.reduce((acc: number, b: any) => acc + b.revenue_sum || 0, 0),
        num: kpiState
          ?.filter((k: any) => k.state === 'Active' || k.state === 'Complete')
          ?.reduce((acc: number, b: any) => acc + b.num_projects || 0, 0),
      },
      {
        id: 'total_realized_revenue',
        title: 'Total Realized Revenue',
        value: realized_revenue?.reduce(
          (acc: number, b: any) => acc + b.realized_revenue_sum || 0,
          0
        ),
        num: realized_revenue?.reduce(
          (acc: number, b: any) => acc + b.num_projects || 0,
          0
        ),
      },
    ],
    [kpiState, realized_revenue]
  );

  const revenueData = useMemo(() => {
    const monte = MonteCarloForecastedRevenue(
      numSimulations,
      forecast?.filter((f: RevenueForecast) => !f.awarded && f.bid)
    );

    const endDate = moment().add(12, 'months'); // one year in the future
    const startDate = moment();
    const data: any = {};

    // Loop through each month from the start date to the current date
    let currentDate = moment(startDate);
    data['Past Due'] = {
      awarded_rev: 0,
      pre_award_rev: 0,
      unknown: 0,
      total: 0,
    };
    while (currentDate.isSameOrBefore(endDate)) {
      const yearMonth = currentDate.format('YYYY-MM');
      data[yearMonth] = {
        awarded_rev: 0,
        pre_award_rev: 0,
        unknown: 0,
        year: currentDate.year,
        month: currentDate.month,
      };

      // Move to the next month
      currentDate.add(1, 'month');
    }
    data['> 1 year out'] = {
      awarded_rev: 0,
      pre_award_rev: 0,
      unknown: 0,
      total: 0,
    };
    data['No Date'] = {
      awarded_rev: 0,
      pre_award_rev: 0,
      unknown: 0,
      total: 0,
    };
    [
      ...(monte || []),
      ...(forecast || []).filter((f: RevenueForecast) => f.awarded || !f.bid),
    ]?.forEach((f: any) => {
      const awarded_rev =
        f.awarded || (!f.bid && f.awarded !== null) ? f.revenue_sum : 0;
      const pre_award_rev = !f.awarded && f.bid ? f.revenue_sum : 0;
      const unknown = f.awarded === null ? f.revenue_sum : 0;
      let key = `${f?.year}-${f.month > 9 ? f.month : `0${f.month}`}`;
      if (f.year > 0) {
        if (
          f.year < startDate.year() ||
          (f.month < startDate.month() + 1 && f.year === startDate.year())
        ) {
          key = 'Past Due';
        } else if (
          f.year > endDate.year() ||
          (f.month > endDate.month() && f.year === endDate.year())
        ) {
          key = '> 1 year out';
        }
      } else {
        key = 'No Date';
      }
      data[key] = {
        awarded_rev: data[key]?.awarded_rev + awarded_rev,
        pre_award_rev: data[key]?.pre_award_rev + pre_award_rev,
        unknown: data[key]?.unknown + unknown,
        total:
          Number(data[key]?.total || 0) +
          Number(awarded_rev || 0) +
          Number(pre_award_rev || 0) +
          Number(unknown || 0),
      };
    });
    return {
      labels: Object.keys(data),
      datasets: [
        {
          label: 'Revenue from awarded jobs',
          data: Object.values(data)?.map((d: any) => d.awarded_rev),
          totals: Object.values(data)?.map((d: any) =>
            !d.unknown && !d.pre_award_rev ? d.total : 0
          ),
          backgroundColor: '#76B041',
        },
        {
          label: 'Expected revenue from Pre-Award jobs',
          data: Object.values(data)?.map((d: any) => d.pre_award_rev),
          totals: Object.values(data)?.map((d: any) =>
            !d.unknown ? d.total : 0
          ),
          backgroundColor: '#163B3F',
        },
        {
          label: 'Revenue from projects with no stage',
          data: Object.values(data)?.map((d: any) => d.unknown),
          totals: Object.values(data)?.map((d: any) =>
            d.unknown ? d.total : 0
          ),
          backgroundColor: 'gray',
        },
      ],
      rawdata: data,
    };
  }, [forecast, numSimulations]);

  const [exportData, exportResult] = useLazyGetForecastAnalyticsExportQuery({
    refetchOnFocus: false,
  });

  const mdDown = useBreakpointQuery('md-down');
  const smDown = useBreakpointQuery('sm-down');
  return (
    <Grid
      container
      flexDirection={'column'}
      spacing={3}
      alignItems={'stretch'}
      wrap={'nowrap'}
      sx={{ mb: 5, pt: '15px', height: '85vh' }}
    >
      <Grid item>
        {/* <Grid item xs={12} sx={{ py: 2 }}> */}
        <Grid
          container
          direction="row"
          alignItems="center"
          spacing={2}
          flexWrap={'nowrap'}
          xs={12}
        >
          <Grid item sx={{ pr: 1 }}>
            <Typography variant="h6">Overview Filters:</Typography>
          </Grid>
          <Grid flex={1} item>
            <JFormik
              initialValues={{}}
              form={[
                {
                  name: 'type',
                  itemType: 'autocomplete',
                  label: 'Project Type',
                  value: projectType,
                  gridProps: {
                    xs: 3,
                  },
                  options: [{ id: 0, name: 'All' }, ...(projectTypes || [])],
                  jItemProps: {
                    onChange: (type: ProjectType) => {
                      setProjectType(type || ({ id: 0, name: 'All' } as any));
                    },
                    // InputProps: {
                    //   sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                    // },
                  },
                },
                {
                  precondition: () =>
                    contactRecord?.form?.authorization === 'Executive' ||
                    contactRecord?.form?.authorization ===
                      'Organization Member' ||
                    contactRecord?.form?.authorization === 'Team Leader',
                  name: 'team',
                  disabled:
                    contactRecord?.form?.authorization === 'Team Leader',
                  itemType: 'autocomplete',
                  label: 'Team',
                  value: team,
                  gridProps: {
                    xs: 3,
                  },
                  options: [{ id: 0, name: 'All' }, ...teams],
                  jItemProps: {
                    onChange: (team: Team) => {
                      setTeam(team || ({ id: 0, name: 'All' } as any));
                    },
                    // InputProps: {
                    //   sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                    // },
                  },
                },
                {
                  name: 'contact',
                  itemType: 'autocomplete',
                  label: 'Individual',
                  value: individual,
                  options: [
                    { id: 0, name: 'All' },
                    ...contacts
                      ?.filter(
                        c =>
                          c.contact_r?.form?.team?.id === team.id ||
                          team.id === 0
                      )
                      ?.map(c => ({
                        ...c.contact_r,
                        name: `${c.contact_r.firstname} ${c.contact_r.lastname}`,
                      })),
                  ],
                  gridProps: { xs: 3 },
                  jItemProps: {
                    onChange: (contact: any) =>
                      setIndividual(contact || ({ id: 0, name: 'All' } as any)),
                    // InputProps: {
                    //   sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                    // },
                  },
                },
              ]}
            />
          </Grid>
          {/* </Grid> */}
        </Grid>
        <Divider sx={{ pt: 1 }} />
      </Grid>
      {/* <Grid item xs={12}>
        <JFormik
          initialValues={{}}
          form={[
            {
              name: 'type',
              itemType: 'autocomplete',
              label: 'Project Type',
              value: projectType,
              gridProps: {
                xs: 3,
              },
              options: [{ id: 0, name: 'All' }, ...projectTypes],
              jItemProps: {
                onChange: (type: ProjectType) => {
                  setProjectType(type || ({ id: 0, name: 'All' } as any));
                },
                InputProps: {
                  sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                },
              },
            },
            {
              precondition: () =>
                contactRecord?.form?.authorization === 'Executive' ||
                contactRecord?.form?.authorization === 'Organization Member',
              name: 'team',
              itemType: 'autocomplete',
              label: 'Team',
              value: team,
              gridProps: {
                xs: 3,
              },
              options: [{ id: 0, name: 'All' }, ...teams],
              jItemProps: {
                onChange: (team: Team) => {
                  setTeam(team || ({ id: 0, name: 'All' } as any));
                },
                InputProps: {
                  sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                },
              },
            },
            {
              name: 'contact',
              itemType: 'autocomplete',
              label: 'Individual',
              value: individual,
              options: [
                { id: 0, name: 'All' },
                ...contacts
                  ?.filter(
                    c =>
                      c.contact_r?.form?.team?.id === team.id || team.id === 0
                  )
                  ?.map(c => ({
                    ...c.contact_r,
                    name: `${c.contact_r.firstname} ${c.contact_r.lastname}`,
                  })),
              ],
              gridProps: { xs: 3 },
              jItemProps: {
                onChange: (contact: any) =>
                  setIndividual(contact || ({ id: 0, name: 'All' } as any)),
                InputProps: {
                  sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                },
              },
            },
          ]}
        />
      </Grid> */}
      <Grid item>
        <Grid container spacing={2}>
          {kpiCards.map(kpiCard => (
            <Grid key={`kpi-card-${kpiCard.id}`} item xs={6} md={3}>
              <KpiCard
                title={kpiCard.title}
                value={kpiCard?.value || 0}
                loading={kpiCardsLoading}
                caption={`${kpiCard.num || 0} projects`}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid flex={1} item>
        <Paper elevation={5} sx={{ height: '100%' }}>
          <Grid
            container
            alignItems="center"
            justifyContent={'space-between'}
            sx={{
              py: 1,
              px: 3,
            }}
          >
            <Grid item>
              <Typography variant="h6">Revenue Forecast</Typography>
            </Grid>
            {/* <Grid item>
               <Grid container direction="row" alignItems="center">
                <Grid item>
                  <JFormik
                    initialValues={{}}
                    form={[
                      {
                        name: 'type',
                        itemType: 'autocomplete',
                        label: '# Simulations',
                        value: `${numSimulations}`,
                        options: ['1000', '10000', '100000'],
                        jItemProps: {
                          // fullwidth: true,
                          onChange: (value: string) => {
                            setNumSimulations(Number(value));
                          },
                          InputProps: {
                            sx: { minWidth: 150 },
                          },
                        },
                      },
                    ]}
                  />
                </Grid>
              </Grid> 
            </Grid> */}
            {/* {exportResult.isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <IconButton
                onClick={() => {
                  exportData({
                    params: {
                      team: team?.id,
                      individual: individual?.id,
                    },
                  });
                }}
                sx={{ ml: 2 }}
                size="large"
              >
                <CloudDownload />
              </IconButton>
            )} */}
          </Grid>

          <Box
            sx={{
              // pb: 1,
              pl: { xs: 1, md: 3 },
              pr: { xs: 2, md: 4 },
              height: '80%',
            }}
          >
            <Bar
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                  padding: {
                    top: 25,
                  },
                },
                plugins: {
                  legend: {
                    display: false,
                    position: 'bottom' as const,
                  },
                  title: {
                    display: false,
                    // text: 'Chart.js Bar Chart',
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        let value = Number(context.raw);
                        if (value >= 1000000) {
                          return (
                            context?.dataset?.label +
                            ': $' +
                            (value / 1000000).toFixed(1) +
                            'M'
                          );
                        } else if (value >= 1000) {
                          return (
                            context?.dataset?.label +
                            ': $' +
                            (value / 1000).toFixed(0) +
                            'K'
                          );
                        } else {
                          return context?.dataset?.label + ': $' + value;
                        }
                      },
                    },
                  },
                  datalabels: {
                    color: 'white', // Color of the data labels
                    display: true, // Set this to true to display data labels
                    align: 'start',
                    anchor: 'end',
                    font: {
                      weight: 'bold',
                    },
                    formatter: function (value, context) {
                      if (value >= 1000000) {
                        return '$' + (value / 1000000).toFixed(1) + 'M';
                      } else if (value >= 1000) {
                        return '$' + (value / 1000).toFixed(1) + 'K';
                      } else if (value > 0) {
                        return '$' + value;
                      } else {
                        return '';
                      }
                    },
                    labels: {
                      total: {
                        color: '#333333', // Color of the data labels
                        display: true, // Set this to true to display data labels
                        align: 'start',
                        offset: -25,
                        anchor: 'end',
                        font: {
                          weight: 'bold',
                        },
                        formatter: function (val, context) {
                          const value = (context?.dataset as any)?.totals?.[
                            context?.dataIndex || 0
                          ];
                          if (value >= 1000000) {
                            return '$' + (value / 1000000).toFixed(1) + 'M';
                          } else if (value >= 1000) {
                            return '$' + (value / 1000).toFixed(1) + 'K';
                          } else if (value > 0) {
                            return '$' + value;
                          } else {
                            return '';
                          }
                        },
                      },
                      // total2: {
                      //   color: 'white', // Color of the data labels
                      //   display: true, // Set this to true to display data labels
                      //   align: 'start',
                      //   // offset: 25,
                      //   anchor: 'end',
                      //   font: {
                      //     weight: 'bold',
                      //   },
                      //   formatter: function (value, context) {
                      //     if (value >= 1000000) {
                      //       return '$' + (value / 1000000).toFixed(1) + 'M';
                      //     } else if (value >= 1000) {
                      //       return '$' + (value / 1000).toFixed(1) + 'K';
                      //     } else if (value > 0) {
                      //       return '$' + value;
                      //     } else {
                      //       return '';
                      //     }
                      //   },
                      // },
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true, // Enable stacking for the X-axis
                    ticks: {
                      // Custom callback function to format Y-axis labels with dollar signs
                      // callback: value => value,
                    },
                  },
                  y: {
                    stacked: true, // Enable stacking for the Y-axis
                    beginAtZero: true,
                    ticks: {
                      // Custom callback function to format Y-axis labels with dollar signs
                      // callback: value =>
                      //   value.toLocaleString('en-US', {
                      //     style: 'currency',
                      //     currency: 'USD',
                      //     minimumFractionDigits: 0,
                      //     maximumFractionDigits: 0,
                      //   }),
                      callback: function (value, index, values) {
                        const nValue = Number(value);
                        if (nValue >= 1000000) {
                          return '$' + (nValue / 1000000).toFixed(1) + 'M';
                        } else if (nValue >= 1000) {
                          return '$' + (nValue / 1000).toFixed(0) + 'K';
                        } else {
                          return '$' + nValue;
                        }
                      },
                    },
                  },
                },
              }}
              data={revenueData}
            />
          </Box>

          <Grid
            container
            flexDirection={smDown ? 'column' : 'row'}
            alignItems={smDown ? 'flex-start' : 'center'}
            justifyContent="space-between"
            sx={{
              pl: { xs: 6, md: 8 },
              pr: { xs: 3, md: 5 },
              pt: 2,
              // height: '10%',
            }}
          >
            <Grid
              item
              flexDirection={mdDown ? 'column' : 'row'}
              alignItems={mdDown ? 'flex-start' : 'center'}
              sx={{ display: 'flex' }}
            >
              <Grid item sx={{ mr: 3, display: 'flex', alignItems: 'center' }}>
                <Box
                  width={12}
                  height={12}
                  bgcolor={'#76B041'}
                  marginRight={1}
                ></Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="leading-none"
                >
                  Revenue from awarded jobs
                </Typography>
              </Grid>

              <Grid
                item
                sx={{
                  mr: 3,
                  mt: mdDown ? 1 : 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  width={12}
                  height={12}
                  bgcolor={'#163B3F'}
                  marginRight={1}
                ></Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="leading-none"
                >
                  Expected revenue from Pre-Award jobs{' '}
                  <small>(Monte Carlo Simulated)</small>
                </Typography>
              </Grid>
              <Grid
                item
                sx={{
                  mt: mdDown ? 1 : 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  width={12}
                  height={12}
                  bgcolor={'gray'}
                  marginRight={1}
                ></Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="leading-none"
                >
                  Revenue from projects with no stage
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      {/* </Grid> */}
      {/* </Grid> */}
    </Grid>
  );
};

// Define the MonteCarloForecastedRevenue function
function MonteCarloForecastedRevenue(
  numSimulations: number,
  revenueForecasts: RevenueForecast[] = []
): RevenueForecast[] {
  // // Set up a random number generator
  // const engine = Random.engines.mt19937().autoSeed();
  // const numSimulations = 10000;
  const numMonths = 13;
  let forecastAverages: RevenueForecast[] = new Array(
    revenueForecasts.length
  ).fill({
    awarded: null,
    revenue_sum: 0,
  } as RevenueForecast);
  const forecastCounts: number[] = new Array(revenueForecasts.length).fill(0);
  const winCounts: number[] = new Array(revenueForecasts.length).fill(0);

  for (let i = 0; i < numSimulations; i++) {
    // for (const pt of projectTypes) {
    for (let j = 0; j < revenueForecasts.length; j++) {
      const forecast = revenueForecasts[j];
      const win_rate = forecast.win_rate ? forecast.win_rate / 100 : 0.5;
      if (Math.random() < win_rate) {
        const randomMonth = Math.floor(Math.random() * numMonths);
        if (
          forecast.month === randomMonth + 1 ||
          (forecast.month === undefined && randomMonth + 1 === 13)
        ) {
          forecastAverages[j] = {
            type: forecast.type,
            win_rate: win_rate,
            awarded: forecast.awarded,
            month: forecast.month,
            year: forecast.year,
            bid: forecast.bid,
            revenue_sum:
              (forecastAverages[j]?.revenue_sum || 0) +
              (forecast.revenue_sum || 0),
          };
          winCounts[j]++;
        }
        forecastCounts[j]++;
      }
    }
    // }
  }
  let forecastResults: RevenueForecast[] = [...forecastAverages];
  for (let i = 0; i < revenueForecasts.length; i++) {
    if (forecastCounts[i] > 0) {
      forecastResults[i].revenue_sum! /= forecastCounts[i];
    }
  }
  return forecastResults;
}

/*// Define the MonteCarloForecastedRevenue function
function MonteCarloForecastedRevenue(
  numSimulations: number,
  revenueForecasts: RevenueForecast[] = []
): RevenueForecast[] {
  // // Set up a random number generator
  // const engine = Random.engines.mt19937().autoSeed();
  // const numSimulations = 10000;
  const numMonths = 13;
  let monthlyAverages:{[key:string]:RevenueForecast} = {};
  const monthCounts: {[key:string]:number} = {};
  const endDate = moment().add(12, 'months'); // one year in the future
    const startDate = moment();
    const data: any = {};

  for (let i = 0; i < numSimulations; i++) {
    // for (const pt of projectTypes) {
    for (const forecast of revenueForecasts) {
      let key = ``;
      if (forecast.year && forecast.year > 0 && forecast.month && forecast.month >= 0) {
        if (
          forecast.year < startDate.year() ||
          (forecast.month < startDate.month() + 1 && forecast.year === startDate.year())
        ) {
          key = 'Past Due';
        } else if (
          forecast.year > endDate.year() ||
          (forecast.month > endDate.month() && forecast.year === endDate.year())
        ) {
          key = '> 1 year out';
        } else {
          key = `${forecast.year}-${forecast.month > 9 ? forecast.month : `0${forecast.month}`}`;
        }
      } else {
        key = 'No Date';
      }

      const win_rate = forecast.win_rate ? forecast.win_rate : 50;
      if (Math.random() < win_rate) {
        const randomMonth = Math.floor(Math.random() * (numMonths));
        if (
          forecast.month === randomMonth + 1 ||
          forecast.month === undefined && randomMonth === 13
        ) {
          monthlyAverages[key] = {
            type: forecast.type,
            win_rate: win_rate,
            awarded: forecast.awarded,
            month: forecast.month,
            year: forecast.year,
            bid: forecast.bid,
            revenue_sum:
              (monthlyAverages[key]?.revenue_sum || 0) +
              (forecast.revenue_sum || 0),
          };
          monthCounts[key]++;
        }
      }
    }
    // }
  }

  for (let i = 0; i < numMonths; i++) {
    if (monthCounts[i] > 0) {
      monthlyAverages[i].revenue_sum! /= monthCounts[i];
    }
  }

  return monthlyAverages;
} */
